import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { OPERATORS_CONFIG_MAP } from '~/bi/constants/bi-constants';

dayjs.extend(utc);
dayjs.extend(timezone);

export function getFilterText(column_config, filter_config) {
  // console.log(':::getFilterText::', column_config, filter_config);
  const column_label = column_config.alias;
  if (!filter_config) {
    return column_label;
  }

  if (filter_config.operator.component === 'none') {
    return `${column_label} ${filter_config.operator.label}`;
  }
  else if (['between', 'not_between'].includes(filter_config.operator.name) && !Array.isArray(filter_config?.operator_value)) {
    return `${column_label} is ${filter_config.operator.label} ${filter_config.operator_value_min} and ${filter_config.operator_value_max}`;
  }
  else if (['between', 'not_between'].includes(filter_config.operator.name) && Array.isArray(filter_config.operator_value)) {
    return `${column_label} is ${filter_config.operator.label} ${filter_config.operator_value[0]} and ${filter_config.operator_value[1]}`;
  }
  else if (['equals', 'does_not_equal', 'greater_than', 'greater_than_or_equal_to', 'less_than', 'less_than_or_equal_to'].includes(filter_config.operator.name)) {
    const value = Array.isArray(filter_config.operator_value) ? filter_config.operator_value[0] : filter_config.operator_value;
    const value_with_count = Array.isArray(filter_config.operator_value) && filter_config.operator_value.length > 1 ? `${filter_config.operator_value.length} values` : value;

    return `${column_label} is ${filter_config.operator.label} ${value_with_count}`;
  }
  else if (filter_config.operator.name === 'in') {
    const selection_text = filter_config.operator_value.length > 1 ? 'selections' : 'selection';
    return `${column_label} is ${filter_config.operator_value.length} ${selection_text}`;
  }
  else if (filter_config.operator.name === 'not_in') {
    const selection_text = filter_config.operator_value.length > 1 ? 'selections' : 'selection';
    return `${column_label} is not ${filter_config.operator_value.length} ${selection_text}`;
  }
  else if (filter_config.operator.name === 'date_is') {
    return `${column_label} is ${filter_config.operator_value.split('_').join(' ')}`;
  }
  else if (['contains', 'does_not_contain', 'starts_with', 'ends_with'].includes(filter_config.operator.name)) {
    return `${column_label} ${filter_config.operator.label} ${filter_config.operator_value}`;
  }
  else if (['contains_any_of', 'contains_all_of', 'contains_exactly', 'contains_none_of'].includes(filter_config.operator.name)) {
    const selection_text = filter_config.operator_value.length > 1 ? 'selections' : 'selection';
    return `${column_label} has ${filter_config.operator_value.length} ${selection_text}`;
  }
  else if (filter_config.operator.name === 'relative_period') {
    return `${column_label} is ${getRelativeToDateSelectionText(filter_config)}`;
  }
  else if (filter_config.operator.name === 'n_to_date') {
    return `${column_label} is ${getNToDateSelectionText(filter_config)}`;
  }
  else {
    return `${column_label} is set`;
  }
}

export function generateNtoDateOperatorVal(config) {
  const { anchor_date_starting_amount, anchor_date_starting_custom_date, anchor_date_starting_point, anchor_date_starting_time_unit } = config;

  let parsed_string;

  if (anchor_date_starting_point === 'later') {
    parsed_string = `${anchor_date_starting_amount}_${anchor_date_starting_time_unit}`;
  }
  if (anchor_date_starting_point === 'earlier') {
    parsed_string = `-${anchor_date_starting_amount}_${anchor_date_starting_time_unit}`;
  }
  if (anchor_date_starting_point === 'custom_date') {
    parsed_string = dayjs(anchor_date_starting_custom_date).format('YYYY-MM-DD');
  }

  return parsed_string;
}

export function generateRelativeToDateOperatorVal(config) {
  const {
    anchor_period_starting_amount,
    anchor_period_time_amount,
    anchor_period_starting_custom_date,
    anchor_period_starting_point,
    anchor_period_starting_time_unit,
    anchor_period_time_unit,
    anchor_period_include_period,
  } = config;

  let query_string;
  let anchor_string;

  // Early return for current period
  if (config.operator_name === 'relative_date_current') {
    return anchor_period_time_unit;
  }

  if (['relative_date_last', 'relative_date_next'].includes(config.operator_name)) {
    const prefix = config.operator_name === 'relative_date_last' ? '-' : '';
    query_string = `${prefix}${anchor_period_time_amount}_${anchor_period_time_unit}`;
  }

  // Early return if starting point is today
  if (config.anchor_period_starting_point === 'today') {
    return [query_string, null, anchor_period_include_period];
  }

  if (anchor_period_starting_point === 'later') {
    anchor_string = `${anchor_period_starting_amount}_${anchor_period_starting_time_unit}`;
  }
  if (anchor_period_starting_point === 'earlier') {
    anchor_string = `-${anchor_period_starting_amount}_${anchor_period_starting_time_unit}`;
  }
  if (anchor_period_starting_point === 'custom_date') {
    anchor_string = dayjs(anchor_period_starting_custom_date).format('YYYY-MM-DD');
  }

  return [query_string, anchor_string];
}

/**
 * Get the operator value with proper formatting fields
 * @param {object} data - The form data containing operator values
 * @param {object} selected_operator - The selected operator configuration
 * @param {string} field_type - The field type (date, timestamp, integer, float, text, etc.)
 * @param {string} user_timezone - The user's timezone (optional, will use browser timezone if not provided)
 * @returns {*} The formatted operator value
 */
export function getOperatorValue(data, selected_operator, field_type, user_timezone = null) {
  let value = data.operator_value;
  const timezone = user_timezone || dayjs.tz.guess();
  const is_date_or_timestamp = ['date', 'timestamp'].includes(field_type);

  if (['between', 'not_between'].includes(selected_operator.name) && ['integer', 'float', 'date', 'timestamp'].includes(field_type)) {
    value = [data.operator_value_min, data.operator_value_max];

    // For date/timestamp fields, convert to start and end of day in user's timezone
    if (is_date_or_timestamp && data.operator_value_min && data.operator_value_max) {
      const start_of_day = dayjs.tz(data.operator_value_min, timezone).startOf('day').toISOString();
      const end_of_day = dayjs.tz(data.operator_value_max, timezone).endOf('day').toISOString();
      value = [start_of_day, end_of_day];
    }
  }

  if (['less_than', 'less_than_or_equal_to', 'greater_than', 'greater_than_or_equal_to'].includes(selected_operator.name) && is_date_or_timestamp && data.operator_value) {
    value = dayjs.tz(data.operator_value, timezone).startOf('day').toISOString();
  }

  if (['is_empty', 'is_not_empty'].includes(selected_operator.name)) {
    value = undefined;
  }
  if (['n_to_date'].includes(selected_operator.name)) {
    value = generateNtoDateOperatorVal(data);
  }
  if (['relative_period'].includes(selected_operator.name)) {
    value = generateRelativeToDateOperatorVal(data);
  }

  return value;
}

function getRelativeToDateSelectionText(filterConfig) {
  const {
    anchor_period_starting_amount,
    anchor_period_starting_custom_date,
    anchor_period_starting_point,
    anchor_period_starting_time_unit,
    anchor_period_time_amount,
    anchor_period_time_unit,
    operator_name,
  } = filterConfig;

  const formatDate = date => dayjs(date).format('YYYY-MM-DD');

  if (operator_name === 'relative_date_current') {
    return `in current ${anchor_period_time_unit}`;
  }

  if (['relative_date_last', 'relative_date_next'].includes(operator_name)) {
    const prefix = operator_name === 'relative_date_last' ? 'last' : 'next';
    const periodText = `${prefix} ${anchor_period_time_amount} ${anchor_period_time_unit}`;

    if (anchor_period_starting_point === 'today') {
      return `in ${periodText} starting from Today`;
    }
    if (anchor_period_starting_point === 'custom_date') {
      return `in ${periodText} starting from ${formatDate(anchor_period_starting_custom_date)}`;
    }

    const anchorText = `${anchor_period_starting_amount} ${anchor_period_starting_time_unit} ${anchor_period_starting_point}`;
    return `in ${periodText} starting from ${anchorText}`;
  }

  return 'in relative period';
}

function getNToDateSelectionText(filterConfig) {
  const {
    anchor_date_starting_amount,
    anchor_date_starting_custom_date,
    anchor_date_starting_point,
    anchor_date_starting_time_unit,
    operator_name,
  } = filterConfig;

  const prefix = operator_name.split('_')[0];
  const formatDate = date => dayjs(date).format('YYYY-MM-DD');

  const startingText = {
    today: 'Today',
    earlier: `${anchor_date_starting_amount} ${anchor_date_starting_time_unit} earlier`,
    later: `${anchor_date_starting_amount} ${anchor_date_starting_time_unit} after`,
    custom_date: anchor_date_starting_custom_date ? formatDate(anchor_date_starting_custom_date) : '',
  }[anchor_date_starting_point];

  return startingText
    ? `in ${prefix} starting from ${startingText}`
    : 'in date range';
}

/**
 * Used to create a new object with the same structure as the original object but with the valueMapping keys mapped to the original keys
 * eg: contains, icontains, starts_with, istarts_with, n_to_date, quarter_to_date, etc. This is needed to re-map the operator value to the correct key when receiving from BE
 *
 * @param {*} originalObject
 * @returns {*} newObject
 */
function createObjectsFromValueMapping(originalObject) {
  const result = { ...originalObject };

  Object.entries(originalObject).forEach(([key, objData]) => {
    if (!objData?.options)
      return;

    Object.values(objData.options).forEach((optionData) => {
      if (!optionData?.valueMapping)
        return;

      // Info: This is assumed that valueMapping is at level options.*.valueMapping
      // Content of the object is same just the key is different
      Object.values(optionData.valueMapping).forEach((mappedName) => {
        if (mappedName !== key && !result[mappedName]) {
          result[mappedName] = {
            ...objData,
          };
        }
      });
    });
  });

  return result;
};

export function getAllOperatorsConfig() {
  return createObjectsFromValueMapping(OPERATORS_CONFIG_MAP);
}

export function hasFilterWithSameName(filters, filter_name) {
  return filters.some(filter => filter.general?.filter_name === filter_name);
}
